import { NullableDateString } from '@superdispatch/dates';
import { useQueryClient } from '@tanstack/react-query';
import { Settings } from 'luxon';
import { useEffect, useMemo } from 'react';
import {
  APIQueryOptions,
  APIQueryResult,
  useAPIQuery,
} from 'shared/api/APIQuery';
import { APIMutationOptions, useAPIMutation } from 'shared/api/APIMutation';
import { requestCarrierAPI } from 'shared/api/CarrierAPIClient';
import { requestCarrierAPIPublic } from 'shared/api/PublicCarrierAPIClient';
import { LocalStore } from 'shared/helpers/Store';
import { VERIFICATION_STATUS } from 'shared/modules/carrier-profile/data/VerifiedCarrierApplicationStatusDTO';
import { Experiment } from 'shared/modules/experiments/ExperimentsAPI';

export type FeatureToggle =
  // Please sort alphabetically.
  | 'admin_user_driver_role_management'
  | 'botpress_support_widget'
  | 'carrier_allow_business_name_updating'
  | 'carrier_duplicate_edi_load'
  | 'carrier_loads_pagination'
  | 'carrier_onboarding_packets'
  | 'carrier_onboarding_packets_webview'
  | 'carrier_restrict_changing_load_id'
  | 'carrier_restrict_shipper_address_editing'
  | 'cd_retired_content'
  | 'combined_send_invoice'
  | 'company_activities'
  | 'ctms_zod_schema'
  | 'ctms_loadboard_zod_schema'
  | 'ctms_claim_management'
  | 'ctms_clean_bol'
  | 'ctms_driver_cdl_verification'
  | 'ctms_edi_setup_vehicles'
  | 'ctms_edi_vehicles'
  | 'ctms_edi_subhaul'
  | 'ctms_gate_pass'
  | 'ctms_homepage'
  | 'ctms_photo_sync'
  | 'ctms_preview_invoice'
  | 'ctms_reporting'
  | 'ctms_reporting_download_report'
  | 'ctms_loads_add_load'
  | 'ctms_loads_address_verification'
  | 'ctms_loads_dropzone'
  | 'ctms_loads_new'
  | 'ctms_loads_options'
  | 'ctms_loads_page_count'
  | 'ctms_native_fuel_map'
  | 'ctms_show_require_inspection_field'
  | 'ctms_subscription_contact'
  | 'ctms_subscription_payment_notifications'
  | 'ctms_sidebar_changes'
  | 'ctms_subhaul_edit_page'
  | 'ctms_suggested_driver_list'
  | 'ctms_supercard_native_application'
  | 'ctms_supercard_tags_spend_policies_split'
  | 'ctms_vin_barcode_generation'
  | 'default_inspection_type'
  | 'supercard_account_switch'
  | 'supercard_management'
  | 'supercard_switch_iframe'
  | 'supercard_switch_redirect'
  | 'payments_ocr_mv_code_extraction'
  | 'payments_superpay_micro_deposit_screenshot'
  | 'new_shipper_alert'
  | 'optional_aiag_inspection'
  | 'password_change_2fa'
  | 'payment_past_due'
  | 'seats_based_pricing'
  | 'slb_edited_by_shipper'
  | 'slb_load_details_dates'
  | 'slb_load_list_scroll_position'
  | 'slb_nearby_shipper_loads'
  | 'slb_suggested_slider_in_available'
  | 'load_animation';

export type FeatureToggles = {
  [F in FeatureToggle]?: null | boolean;
};

export type SuperPayVerificationStatus =
  | 'restricted'
  | 'not_started'
  | 'bank_account'
  | 'bank_account_verification'
  | 'verified'
  | 'bank_account_failed'
  | 'bank_account_errored';

export interface CarrierSettings {
  timezone: string;
  timezone_offset: number; // timezone offset in minutes
  has_terminal: boolean;
  features: FeatureToggles;
  vehicle_types: Record<string, string>;
  paid_methods: Array<{ value: number; text: string }>;
  is_paying: boolean;
  is_eligible_for_driver_verification: boolean;
  is_super_carrier: boolean;
  groups: Experiment[] | null;
  can_show_shipper_info_in_bol: boolean;
  can_create_load: boolean;
  can_import_load: boolean;
  can_open_reporting: boolean;
  can_open_claim: boolean;
  can_dispatch_to_carrier: boolean;
  company_type: 'owner_operator' | 'owner_operator_pro' | 'fleet';
  account_admin?: { guid: string; email: string };
  bol_templates: Array<{
    key: string;
    applicable_to_inspection_types: string[] | null;
    description: string;
    title: string;
  }>;
  carrier: {
    guid: string;
    name: string;
    largest_trailer_capacity: number | null;
    verification_status: (typeof VERIFICATION_STATUS)[number];
    cargo_insurance_expiration_date: NullableDateString;
    is_insurance_renewal_initiated: boolean;
    operation_type: 'Intrastate' | 'Interstate' | null;
  };
  user: {
    guid: string;
    user_hash: string;
    has_access_to_reports: boolean;
    has_accounting_feature: boolean;
    has_permission_to_rate: boolean;
  };
  rating: { percentage: number; count: number };
  superpay: SuperPayVerificationSettings;
  drivers_can_edit_carrier_info: boolean;
  is_fmcsa_phone_verified: boolean;
}

export interface SuperPayBankErrorNotifications {
  name: string;
  code: string;
  action: string;
  description: string | null;
}

interface SuperPayVerificationSettings {
  verification: SuperPayVerificationStatus;
  is_verification_available: boolean;
  alert_superpay_load: boolean;
  is_superpay_requested: boolean;
  is_invite_available: boolean;
  show_invite_banner_until: Date | null;
  instant_transfer_nudging_enabled: boolean;
  instant_transfer_available?: boolean;
  instant_transfer_invitation_load_guid: string | null;
  payment_error: SuperPayBankErrorNotifications | null;
}

const carrierSettingsStore = {
  set: (data: CarrierSettings | undefined): void => {
    LocalStore.set('carrier_settings', JSON.stringify(data));
  },
  get: (): CarrierSettings | undefined => {
    const value = LocalStore.get('carrier_settings');
    return value ? (JSON.parse(value) as CarrierSettings) : undefined;
  },
};

interface CarrierSettingsOptions {
  enableCache?: boolean;
}
export function getApplicableBolTemplates(
  bolTemplates: CarrierSettings['bol_templates'] | undefined,
  inspectionType: string | undefined,
): CarrierSettings['bol_templates'] {
  if (!bolTemplates?.length || !inspectionType) {
    return [];
  }

  return bolTemplates.filter(
    ({ applicable_to_inspection_types }) =>
      applicable_to_inspection_types === null ||
      applicable_to_inspection_types.includes(inspectionType),
  );
}

export function useCarrierSettingsMutation(
  options?: APIMutationOptions<undefined, CarrierSettings>,
) {
  return useAPIMutation<undefined, CarrierSettings>(
    () => requestCarrierAPI('GET /internal/web/carriers/settings/'),
    options,
  );
}

export function useCarrierSettings({
  enableCache = false,
}: CarrierSettingsOptions = {}): APIQueryResult<CarrierSettings> {
  const settings = useAPIQuery<CarrierSettings>(
    ['settings', 'carrier'],
    () => requestCarrierAPI('GET /internal/web/carriers/settings/'),
    {
      staleTime: Infinity,
      gcTime: Infinity,
      ...(enableCache && {
        placeholderData: () => carrierSettingsStore.get(),
      }),
    },
  );

  useEffect(() => {
    if (settings.data && enableCache) {
      carrierSettingsStore.set(settings.data);
    }
  }, [settings.data, enableCache]);

  useEffect(() => {
    if (settings.data?.timezone) {
      Settings.defaultZone = settings.data.timezone;
    }
  }, [settings.data?.timezone]);

  return settings;
}
export function useCarrierSettingsCache() {
  const queryClient = useQueryClient();

  return useMemo(() => {
    function invalidateCarrierSettings() {
      void queryClient.invalidateQueries({ queryKey: ['settings', 'carrier'] });
    }

    function updateCarrierSettingsCache(
      fn: (value: CarrierSettings) => CarrierSettings,
    ) {
      queryClient.setQueryData<CarrierSettings | undefined>(
        ['settings', 'carrier'],
        (prev) => {
          if (!prev) return;

          return fn(prev);
        },
      );
    }

    return { invalidateCarrierSettings, updateCarrierSettingsCache };
  }, [queryClient]);
}

export function useCarrierPaidMethodOptions(): Map<number, string> {
  const { data } = useCarrierSettings();
  return useMemo(
    () => new Map(data?.paid_methods.map(({ text, value }) => [value, text])),
    [data],
  );
}

export function useCarrierPaidMethodVisibleOptions(
  paidMethodId?: number,
): Map<number, string> {
  const { data } = useCarrierSettings();
  return useMemo(
    () =>
      new Map(
        data?.paid_methods
          .filter((method) =>
            paidMethodId ? paidMethodId === method.value : !!method,
          )
          .map(({ text, value }) => [value, text]),
      ),
    [data, paidMethodId],
  );
}

export function useGlobalFeatures({
  staleTime = Infinity,
  gcTime = Infinity,
  ...options
}: APIQueryOptions<FeatureToggles> = {}): APIQueryResult<FeatureToggles> {
  return useAPIQuery<FeatureToggles>(
    ['features'],
    () => requestCarrierAPIPublic('GET /internal/web/features/'),
    { ...options, staleTime, gcTime },
  );
}

export function useCarrierFeatures(): null | FeatureToggles {
  const settings = useCarrierSettings();
  const features = useGlobalFeatures({ enabled: !!settings.error });

  return settings.data?.features ?? features.data ?? null;
}
